import { Fragment } from "react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "@/lib/utils";

export const cinematicEase = [0.22, 1, 0.36, 1] as const;

interface RevealTextProps {
  text: string;
  as?: "h1" | "h2" | "h3" | "p" | "span";
  className?: string;
  wordClassName?: string;
  delay?: number;
  stagger?: number;
  duration?: number;
  /** Animate on mount (default) or when scrolled into view. */
  trigger?: "mount" | "inView";
}

/**
 * Masked, word-by-word title reveal: each word rises out of its own clipped
 * line box, like a film title card. Screen readers get the plain sentence.
 */
export default function RevealText({
  text,
  as = "span",
  className,
  wordClassName,
  delay = 0,
  stagger = 0.07,
  duration = 1.05,
  trigger = "mount",
}: RevealTextProps) {
  const reduceMotion = useReducedMotion();
  const Tag = motion[as];
  const words = text.split(" ");

  const play =
    trigger === "mount"
      ? { animate: "shown" }
      : { whileInView: "shown", viewport: { once: true, amount: 0.6 } };

  return (
    <Tag
      className={className}
      aria-label={text}
      initial={reduceMotion ? false : "hidden"}
      {...play}
      transition={{ staggerChildren: stagger, delayChildren: delay }}
    >
      {words.map((word, index) => (
        <Fragment key={`${word}-${index}`}>
          <span
            aria-hidden="true"
            className="-mb-[0.14em] inline-block overflow-hidden pb-[0.14em] align-bottom"
          >
            <motion.span
              className={cn(
                "inline-block will-change-transform",
                wordClassName
              )}
              variants={{
                hidden: { y: "108%", rotate: 3, filter: "blur(6px)" },
                shown: { y: "0%", rotate: 0, filter: "blur(0px)" },
              }}
              transition={{ duration, ease: cinematicEase }}
            >
              {word}
            </motion.span>
          </span>
          {index < words.length - 1 && " "}
        </Fragment>
      ))}
    </Tag>
  );
}
